import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Navbar } from '../../component/shared/Navbar'
import { PageContainer } from '../../component/styled/PageContainer'
import { useGetWhatsappBroadcastJob } from '../../api/whatsapp-broadcast'

const exportFailedCsv = (job) => {
  if (!job?.failed?.length) return

  const maxVars = job.failed[0].variables.length

  const header = [
    'phoneNumber',
    ...Array.from({ length: maxVars }, (_, i) => `variable${i + 1}`),
    'errorMessage',
  ]

  const lines = [
    header.join(','),
    ...job.failed.map((r) =>
      [
        r.phoneNumber,
        ...Array.from({ length: maxVars }, (_, i) => r.variables?.[i] || ''),
        r.errorMessage,
      ].join(','),
    ),
  ]

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })

  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = `whatsapp-broadcast-failed-${job._id}.csv`
  a.click()

  URL.revokeObjectURL(url)
}

export const WhatsappBroadcastProgressScreen = () => {
  const navigate = useNavigate()
  const { jobId } = useParams()
  const [searchParams] = useSearchParams()

  const doneParam = searchParams.get('done') === 'true'

  const { data: job, isLoading } = useGetWhatsappBroadcastJob(jobId, {
    enabled: !!jobId,
    refetchInterval: doneParam ? false : 3000,
    refetchOnWindowFocus: !doneParam,
  })

  useEffect(
    () => job && job.isDone && !doneParam && navigate(`?done=true`, { replace: true }),
    [job, doneParam, navigate],
  )

  if (isLoading)
    return (
      <>
        <Navbar />
        <PageContainer>
          <Typography my={3}>Loading...</Typography>
        </PageContainer>
      </>
    )

  if (!job)
    return (
      <>
        <Navbar />
        <PageContainer>
          <Box my={3}>
            <Alert severity='error'>Job not found.</Alert>
          </Box>
        </PageContainer>
      </>
    )

  const total = Number(job.totalRows || 0)
  const sent = Number(job.sentCount || 0)
  const failed = Number(job.failedCount || 0)
  const processed = sent + failed

  const progress = total ? Math.min(100, (processed / total) * 100) : 0

  return (
    <>
      <Helmet titleTemplate='%s | Staff Portal'>
        <title>Broadcast Progress</title>
      </Helmet>
      <Navbar />
      <PageContainer>
        <Box my={3} display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h1'>Broadcast Progress</Typography>
          <Button variant='outlined' onClick={() => navigate('/whatsapp-broadcast')}>
            Back to Broadcast
          </Button>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Typography>
            Template: <b> {job.templateName} </b>
          </Typography>
          <Typography>
            Sender: <b>{job.senderPhoneNumber}</b>
          </Typography>
          <Typography>
            Status: <b>{job.status}</b>
          </Typography>
          <Box mt={2}>
            <LinearProgress variant='determinate' value={progress} />
            <Typography mt={1}>
              Progress: {processed}/{total} ({progress.toFixed(1)}%)
            </Typography>
            <Typography>
              Sent: <b>{sent}</b>, Failed: <b>{failed}</b>
            </Typography>
          </Box>
          {doneParam && (
            <Alert severity='success' sx={{ mt: 2 }}>
              Broadcast finished.{' '}
            </Alert>
          )}
          {!doneParam && (
            <Alert severity='info' sx={{ mt: 2 }}>
              Processing in background. Safe to refresh.
            </Alert>
          )}
          {!!job.failed?.length && (
            <Box mt={3}>
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography variant='h5'>Failed Rows</Typography>
                {doneParam && (
                  <Button size='small' variant='outlined' onClick={() => exportFailedCsv(job)}>
                    Export Failed CSV
                  </Button>
                )}
              </Box>
              <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 420 }}>
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Phone Number</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {job.failed.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.phoneNumber}</TableCell>
                        <TableCell>{r.errorMessage}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </PageContainer>
    </>
  )
}
